import { useQuery } from '@tanstack/react-query';

import { adminFetch } from '@/hooks/admin/useAdminApi';

/** Store identity stamped onto every row by the server. */
export interface GroupStoreStamp {
  customer_id: string;
  store_name: string;
  store_code: string | null;
  is_master: boolean;
}

export interface GroupEquipmentRow extends GroupStoreStamp {
  id: string;
  asset_tag: string | null;
  serial_number: string | null;
  name: string;
  category: string | null;
  make: string | null;
  model_number: string | null;
  qty: number;
  monthly_rent: number;
  ownership_type: string | null;
  status: string | null;
  is_loaner: boolean;
  installed_at: string | null;
  removed_at: string | null;
  image_url: string | null;
  on_site: boolean;
}

export interface GroupContractRow extends GroupStoreStamp {
  id: string;
  contract_number: string | null;
  contract_type: string | null;
  category: string | null;
  status: string | null;
  start_date: string | null;
  end_date: string | null;
  monthly_equipment_amount: number;
  monthly_service_amount: number;
  external_document_url: string | null;
}

export interface GroupServiceRequestRow extends GroupStoreStamp {
  id: string;
  kind: string;
  subject: string;
  body: string | null;
  status: string;
  sf_ticket_id: string | null;
  created_at: string;
  updated_at: string;
  is_open: boolean;
}

export interface GroupCylinderRow extends GroupStoreStamp {
  label: string;
  balance: number;
  unit_price: number;
  monthly_rent: number;
  btrf_doc_number: string | null;
  btrf_date: string | null;
}

export interface GroupAssetsResponse {
  ok: true;
  is_group: boolean;
  master?: {
    customer_id: string;
    name: string;
    group_kind: 'chain' | 'franchise';
    group_brand: string | null;
  };
  stores: GroupStoreStamp[];
  equipment: GroupEquipmentRow[];
  contracts: GroupContractRow[];
  service_requests: GroupServiceRequestRow[];
  cylinders: GroupCylinderRow[];
  totals?: {
    stores: number;
    equipment_on_site: number;
    equipment_monthly_rent: number;
    contracts: number;
    service_open: number;
    service_total: number;
    cylinders_out: number;
    cylinder_monthly_rent: number;
  };
}

/**
 * What the chain HAS, across every store — equipment, contracts, service
 * tickets and CO₂ balances, each row stamped with its store.
 *
 * `is_group: false` means the caller is standing on an ordinary account (or is
 * not the chain's AP/admin), and the page renders its single-account version.
 * That is a normal answer, not an error.
 */
export function useGroupAssets() {
  return useQuery<GroupAssetsResponse>({
    queryKey: ['group-assets'],
    queryFn: () => adminFetch<GroupAssetsResponse>('customer-group-assets'),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}
